import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Users, UserCheck, UserX, UserPlus, Info } from 'lucide-react';
import UserManagement from '@/pages/UserManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KpiCard = ({ icon: Icon, label, value, tone, testid }) => {
  const tones = {
    teal: 'from-teal-500 to-emerald-600',
    emerald: 'from-emerald-500 to-green-600',
    rose: 'from-rose-500 to-pink-600',
    sky: 'from-sky-500 to-indigo-500',
  };
  return (
    <Card data-testid={testid} className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center text-white shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 truncate">{label}</div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">{value}</div>
        </div>
      </div>
    </Card>
  );
};

const HRDashboard = () => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/users`)
      .then((res) => { if (alive) setUsers(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { /* roster KPIs are non-critical chrome */ });
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    const total = users.length;
    const inactive = users.filter((u) => u.is_active === false).length;
    const active = total - inactive;
    const monthKey = new Date().toISOString().slice(0, 7);
    const newThisMonth = users.filter((u) => String(u.created_at || '').slice(0, 7) === monthKey).length;
    return { total, active, inactive, newThisMonth };
  }, [users]);

  return (
    <div className="space-y-6 max-w-7xl" data-testid="hr-dashboard">
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
          <Users className="h-7 w-7 text-teal-600" />HR Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Team roster and onboarding.</p>
      </div>

      {/* Roster KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="hr-kpi-total" icon={Users} tone="teal" label="Total Members" value={stats.total} />
        <KpiCard testid="hr-kpi-active" icon={UserCheck} tone="emerald" label="Active" value={stats.active} />
        <KpiCard testid="hr-kpi-inactive" icon={UserX} tone="rose" label="Inactive" value={stats.inactive} />
        <KpiCard testid="hr-kpi-new" icon={UserPlus} tone="sky" label="New This Month" value={stats.newThisMonth} />
      </div>

      {/* Honest note: per-agent performance metrics need agent attribution on
          leads/trips, which the data model doesn't capture yet. */}
      <div data-testid="hr-perf-note" className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
        <Info className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
        <span>Per-agent performance analytics (conversion / productivity) will appear here once lead &amp; trip ownership is tracked per agent. Revenue figures are admin-only and are never shown on this dashboard.</span>
      </div>

      {/* Team roster + onboarding (HR-scoped: create agent/accounts, edit basic
          profile, activate/deactivate — role changes stay admin-only). */}
      <Card className="p-5 sm:p-6 bg-white rounded-2xl border border-slate-100 shadow-sm" data-testid="hr-team-section">
        <UserManagement />
      </Card>
    </div>
  );
};

export default HRDashboard;
